/**
 * Numele paginii din bara de sus.
 *
 * Scos din `AppLayout` ca să fie **verificabil**. Regula lui a căzut de două ori
 * pe același lucru — rezerva era șirul gol — iar un `<h1>` gol nu se vede și nu
 * se plânge: arată exact ca unul plin, pentru oricine se uită la ecran. Doar un
 * cititor de ecran află, și abia atunci când cineva ajunge acolo.
 *
 * Prima dată au fost `/waitlist` și lista de vizite a unui pacient: rute la care
 * se ajunge din bannerul Dashboard-ului și din calendar, deci niciodată din bara
 * laterală, deci fără etichetă (`aa96fad`). A doua dată a rămas **cazul
 * implicit** — `path="*"`, ecranul „Pagina nu există". Ironia întreagă: singura
 * pagină la care ajungi din greșeală era și singura fără nume.
 *
 * `NAV` rămâne în `AppLayout.tsx`, unde `check:navperms` îl citește prin regex;
 * aici se primește ca date.
 */

export interface TitledRoute {
  to: string;
  label: string;
  /** `/` se potrivește exact — altfel ar prinde fiecare rută din aplicație. */
  end?: boolean;
}

/**
 * Rutele care nu sunt în bara laterală dar au o adresă pe care o poate avea
 * cineva. Ordinea contează: ruta mai lungă se încearcă prima.
 */
// Values are `common` translation keys, resolved with t() at the render site
// (same as the NAV labels this reads from).
const EXTRA_TITLES: Array<[string, string]> = [
  ['/waitlist', 'navListaDeAsteptare'],
  // Cererile pacienților noi: scoase din bară, deschise din Centrul de comandă.
  ['/inquiries', 'navCereri'],
  ['/patients/', 'navPacienti'],
];

/**
 * Rezerva e **numele clinicii**, fiindcă e singurul lucru adevărat despre orice
 * rută, inclusiv una adăugată mâine și uitată aici. „Pagina nu există" ar fi
 * fost mai precis pe `*` și greșit pe ruta uitată — iar o etichetă greșită e
 * mai rea decât una generală.
 */
export function pageTitleFor(
  pathname: string,
  nav: readonly TitledRoute[],
  clinicName: string,
  /**
   * Traducerea etichetelor din bară. Din 18.09 `NAV` ține chei de traducere
   * (`navSetari`), nu cuvinte: fără ea bara de sus scria cheia — „navSetari" —
   * în timp ce meniul lateral, care le traduce, arăta „Setări".
   */
  translate: (key: string) => string = (key) => key,
): string {
  const fromNav = nav.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)))?.label;
  if (fromNav) return translate(fromNav);

  // Și rezervele sunt chei de traducere, din aceeași zi.
  const extra = EXTRA_TITLES.find(([prefix]) => pathname.startsWith(prefix))?.[1];
  if (extra) return translate(extra);

  return clinicName;
}
